import { Injectable } from '@angular/core';
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc, Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';
import { CsvDataService } from './csv-data.service';
import { FirebaseService } from './firebase.service';
import { MarketLevelsService } from './market-levels.service';
import { RangeEstimationRow, RangeEstimationsService } from './range-estimations.service';

export interface EconRow {
  date: string;
  economicEvent: string;
}

interface BacktestSample {
  absError: number;
  pctError: number;
  contained: boolean;
  biasPercent: number;
}

export interface BacktestMetric {
  ticker: string;
  model: string;
  n: number;
  avgAbsError: number;
  mapePercent: number;
  containedRatePercent: number;
  biasPercent: number;
}

export interface RangeAndDeltaRow {
  ticker: string;
  forecastFor: string;
  basedOnDate: string;
  /** HAR+EWMA range forecast, IV-adjusted — the single canonical value (see /insights/forecast-backtest for why). */
  forecastRange: number | null;
}

const TICKER_ORDER = ['NQ', 'ES', 'RTY', 'GC', 'QQQ', 'SPY', 'IWM'];
// HAR needs 20 rows + 1 forecast date
const MAX_ROWS_PER_TICKER = 21;
const FORECAST_TTL_MS = 30 * 60 * 1000; // 30 minutes
const ECON_TTL_MS     = 24 * 60 * 60 * 1000; // 24 hours

@Injectable({ providedIn: 'root' })
export class RangeAndDeltaService {
  private db: FirebaseService['db'];

  // Session caches — avoids re-fetching on repeated chatbot queries
  private forecastCache = new Map<string, { rows: RangeAndDeltaRow[]; ts: number }>();
  private econCache: { map: Map<string, EconRow[]>; ts: number } | null = null;
  private holidayCache: { dates: Set<string>; list: { date: string; name: string }[]; ts: number } | null = null;

  constructor(
    private rangeEstimations: RangeEstimationsService,
    private marketLevels: MarketLevelsService,
    private csv: CsvDataService,
    firebase: FirebaseService,
  ) {
    this.db = firebase.db;
  }

  /** Read a single ticker's stored forecast — 1 Firestore read. Returns null if not yet saved. */
  async getStoredForecast(ticker: string): Promise<RangeAndDeltaRow | null> {
    const snap = await getDoc(doc(this.db, 'rangeAndDeltaLatest', ticker));
    if (!snap.exists()) return null;
    const d = snap.data() as RangeAndDeltaRow;
    return {
      ticker: d.ticker,
      forecastFor: d.forecastFor,
      basedOnDate: d.basedOnDate,
      forecastRange: d.forecastRange ?? null,
    };
  }

  /** Read pre-computed forecasts for multiple tickers — one getDoc per ticker, no collection query. */
  async getStoredForecasts(tickers: string[]): Promise<RangeAndDeltaRow[]> {
    const snaps = await Promise.all(tickers.map(t => getDoc(doc(this.db, 'rangeAndDeltaLatest', t))));
    return snaps
      .filter(snap => snap.exists())
      .map(snap => {
        const d = snap.data() as RangeAndDeltaRow;
        return { ticker: d.ticker, forecastFor: d.forecastFor, basedOnDate: d.basedOnDate, forecastRange: d.forecastRange ?? null };
      });
  }

  /** Save computed forecast rows — overwrites latest snapshot and archives one copy per forecastFor date. */
  async saveForecasts(rows: RangeAndDeltaRow[]): Promise<void> {
    const savedAt = Timestamp.now();
    await Promise.all([
      ...rows.map(row =>
        setDoc(doc(this.db, 'rangeAndDeltaLatest', row.ticker), { ...row, savedAt })
      ),
      ...rows.map(row =>
        setDoc(doc(this.db, 'rangeAndDeltaHistory', `${row.ticker}_${row.forecastFor}`), { ...row, savedAt })
      ),
    ]);
  }

  /** Retrieve archived forecast for a specific ticker + date (the date the forecast was FOR). */
  async getHistoricalForecast(ticker: string, date: string): Promise<RangeAndDeltaRow | null> {
    const snap = await getDoc(doc(this.db, 'rangeAndDeltaHistory', `${ticker}_${date}`));
    if (!snap.exists()) return null;
    const d = snap.data() as RangeAndDeltaRow;
    return {
      ticker: d.ticker,
      forecastFor: d.forecastFor,
      basedOnDate: d.basedOnDate,
      forecastRange: d.forecastRange ?? null,
    };
  }

  async computeForecasts(tickers: string[] = TICKER_ORDER): Promise<RangeAndDeltaRow[]> {
    const cacheKey = [...tickers].sort().join(',');
    const cached = this.forecastCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < FORECAST_TTL_MS) return cached.rows;

    // If requesting a subset, check if the full 7-ticker cache already exists (populated by Range & Delta page)
    const fullKey = [...TICKER_ORDER].sort().join(',');
    if (cacheKey !== fullKey) {
      const fullCache = this.forecastCache.get(fullKey);
      if (fullCache && Date.now() - fullCache.ts < FORECAST_TTL_MS) {
        return fullCache.rows.filter(r => tickers.includes(r.ticker));
      }
    }

    // Always compute all 7 tickers at once — one Firestore trip covers all chatbot queries
    const fetchTickers = cacheKey === fullKey ? tickers : TICKER_ORDER;
    const allRows = await this.rangeEstimations.loadRowsByTickers('daily', fetchTickers);
    const sourceRows = this.sliceToRecent(allRows, fetchTickers);

    await this.loadHolidayDates();

    const rows = await this.buildRows(sourceRows, fetchTickers);
    this.forecastCache.set(fullKey, { rows, ts: Date.now() });
    return rows.filter(r => tickers.includes(r.ticker));
  }

  /**
   * Walk-forward backtest: at each historical date, forecast using only rows known before that
   * date, then compare to what actually happened. Runs the same buildForecast/buildHarForecast
   * code paths used in production so the numbers reflect real accuracy, not a re-implementation.
   * Kept around (surfaced at /insights/forecast-backtest) so the canonical model can be
   * re-validated whenever the econ multiplier or base formula is recalibrated.
   */
  async backtestModels(tickers: string[] = TICKER_ORDER): Promise<BacktestMetric[]> {
    const allRows = await this.rangeEstimations.loadRowsByTickers('daily', tickers);
    const econByDate = await this.loadEconMapCached();
    return this.runBacktest(allRows, econByDate, tickers);
  }

  private runBacktest(allRows: RangeEstimationRow[], econByDate: Map<string, EconRow[]>, tickers: string[]): BacktestMetric[] {
    const buckets = new Map<string, { ticker: string; model: string; samples: BacktestSample[] }>();
    for (const ticker of tickers) {
      const sorted = allRows
        .filter(r => r.ticker === ticker && r.date && r.range > 0)
        .sort((a, b) => this.dateValue(a.date) - this.dateValue(b.date));

      for (let i = 20; i < sorted.length; i++) {
        const actual = sorted[i];
        const priorDesc = sorted.slice(Math.max(0, i - 21), i).slice().reverse();
        if (priorDesc.length < 20) continue;

        const forecastIv = actual.iv > 0 ? actual.iv : null;
        const simple = this.buildForecast(priorDesc, forecastIv);
        const har = this.buildHarForecast(priorDesc, forecastIv);
        const econMultiplier = this.computeEcon(actual.date, econByDate).multiplier;

        if (simple) this.recordSample(buckets, ticker, 'Simple (IV)', simple.forecastRange, actual.range);
        if (har) {
          this.recordSample(buckets, ticker, 'HAR (IV)', har.forecastRange, actual.range);
          this.recordSample(buckets, ticker, 'HAR (IV) + Econ', har.forecastRange * econMultiplier, actual.range);
        }
      }
    }

    const results: BacktestMetric[] = [];
    for (const bucket of buckets.values()) {
      if (bucket.samples.length === 0) continue;
      results.push({
        ticker: bucket.ticker,
        model: bucket.model,
        n: bucket.samples.length,
        avgAbsError: this.avg(bucket.samples.map(s => s.absError)),
        mapePercent: this.avg(bucket.samples.map(s => s.pctError)),
        containedRatePercent: (bucket.samples.filter(s => s.contained).length / bucket.samples.length) * 100,
        biasPercent: this.avg(bucket.samples.map(s => s.biasPercent)),
      });
    }
    return results;
  }

  private recordSample(
    buckets: Map<string, { ticker: string; model: string; samples: BacktestSample[] }>,
    ticker: string,
    model: string,
    forecast: number,
    actual: number,
  ): void {
    if (!(forecast > 0) || !(actual > 0)) return;
    const key = ticker + '::' + model;
    const bucket = buckets.get(key) ?? { ticker, model, samples: [] as BacktestSample[] };
    const error = actual - forecast;
    bucket.samples.push({
      absError: Math.abs(error),
      pctError: (Math.abs(error) / actual) * 100,
      contained: actual <= forecast,
      biasPercent: (error / actual) * 100,
    });
    buckets.set(key, bucket);
  }

  /** Keep only the most recent MAX_ROWS_PER_TICKER rows per ticker to cap lookups. */
  private sliceToRecent(rows: RangeEstimationRow[], tickers: string[]): RangeEstimationRow[] {
    return tickers.flatMap(ticker =>
      rows
        .filter(r => r.ticker === ticker && r.date && r.range > 0)
        .sort((a, b) => this.dateValue(b.date) - this.dateValue(a.date))
        .slice(0, MAX_ROWS_PER_TICKER)
    );
  }

  private async loadEconMapCached(): Promise<Map<string, EconRow[]>> {
    if (this.econCache && Date.now() - this.econCache.ts < ECON_TTL_MS) {
      return this.econCache.map;
    }
    const map = await this.loadEconMap();
    this.econCache = { map, ts: Date.now() };
    return map;
  }

  private async buildRows(rows: RangeEstimationRow[], tickers: string[]): Promise<RangeAndDeltaRow[]> {
    const comparisonRows = await Promise.all(tickers.map(async ticker => {
      const sorted = rows
        .filter(row => row.ticker === ticker && row.date && row.range > 0)
        .sort((a, b) => this.dateValue(b.date) - this.dateValue(a.date));

      if (sorted.length < 20) return null;

      const latest = sorted[0];
      const forecastDate = this.nextTradingDate(latest.date);
      const forecastIv = await this.loadForecastDailyIv(ticker, forecastDate);
      const forecast = this.buildHarForecast(sorted, forecastIv);

      return {
        ticker,
        forecastFor: forecastDate,
        basedOnDate: latest.date,
        forecastRange: forecast?.forecastRange ?? null,
      };
    }));

    return comparisonRows.filter((row): row is RangeAndDeltaRow => row !== null);
  }

  /** Simple weighted-average model — kept only as the backtest's comparison baseline. */
  private buildForecast(rows: RangeEstimationRow[], forecastIv: number | null): { forecastRange: number } | null {
    if (rows.length < 10) return null;

    const latest = rows[0];
    const last5 = rows.slice(0, 5);
    const last10 = rows.slice(0, 10);
    const avgRange5 = this.avg(last5.map(row => row.range));
    const avgRange10 = this.avg(last10.map(row => row.range));
    const avgRangePercent5 = this.avg(last5.map(row => this.rowRangePercent(row)));
    const avgRangePercent10 = this.avg(last10.map(row => this.rowRangePercent(row)));
    const volatility = this.volatilityState(latest, avgRangePercent5, avgRangePercent10);
    const setup = this.trendSetup(last5, latest, avgRangePercent10, volatility);
    const stateMultiplier = volatility === 'Expanding' ? 1.08 : volatility === 'Contracting' ? 0.92 : 1;
    const trendMultiplier = setup === 'Bull Trend' || setup === 'Bear Trend' ? 1.04 : 1;
    const baseRange = avgRange5 * 0.65 + avgRange10 * 0.35;

    const ivAnchor = this.avg(last10.map(row => row.iv).filter(v => v > 0));
    const ivInput = typeof forecastIv === 'number' && forecastIv > 0 ? forecastIv : latest.iv;
    const ivRatio = ivAnchor > 0 ? this.clamp(ivInput / ivAnchor, 0.85, 1.2) : 1;
    return { forecastRange: baseRange * ivRatio * stateMultiplier * trendMultiplier };
  }

  /** HAR-RV + EWMA blend, IV-adjusted via OLS beta — the canonical forecasting model. */
  private buildHarForecast(rows: RangeEstimationRow[], forecastIv: number | null): { forecastRange: number } | null {
    if (rows.length < 20) return null;

    const last5 = rows.slice(0, 5);
    const last20 = rows.slice(0, 20);

    const avg5 = this.avg(last5.map(r => r.range));
    const avg20 = this.avg(last20.map(r => r.range));
    const harBase = 0.15 * rows[0].range + 0.55 * avg5 + 0.30 * avg20;
    const ewmaBase = this.computeEwma(last20.map(r => r.range).reverse(), 0.94);
    const blendedBase = 0.70 * harBase + 0.30 * ewmaBase;

    const rangePercents20 = last20.map(r => this.rowRangePercent(r));
    const avgRangePercent5 = this.avg(last5.map(r => this.rowRangePercent(r)));
    const avgRangePercent20 = this.avg(rangePercents20);
    const stdRangePercent20 = this.stdDev(rangePercents20);
    const z = stdRangePercent20 > 0 ? (avgRangePercent5 - avgRangePercent20) / stdRangePercent20 : 0;
    const volatilityMultiplier = 1 + 0.10 * Math.tanh(0.8 * z);

    const upDays = last5.filter(r => r.closeToCloseMove > 0).length;
    const downDays = last5.filter(r => r.closeToCloseMove < 0).length;
    const latestMovePercent = rows[0].previousClose > 0 ? (rows[0].closeToCloseMove / rows[0].previousClose) * 100 : 0;
    const isTrending = (upDays >= 4 && latestMovePercent > 0) || (downDays >= 4 && latestMovePercent < 0);
    const streakStrength = Math.max(upDays, downDays) / 5;
    const trendMultiplier = isTrending ? 1 + 0.06 * streakStrength : 1;

    const rangeRatios = last20.map(r => avg20 > 0 ? r.range / avg20 : 1);
    const ivValues = last20.map(r => r.iv).filter(v => v > 0);
    const medianIv = this.median(ivValues);
    const ivInput = typeof forecastIv === 'number' && forecastIv > 0 ? forecastIv : rows[0].iv;
    const ivRatio = medianIv > 0 ? ivInput / medianIv : 1;
    const ivRatioSeries = last20.map(r => medianIv > 0 && r.iv > 0 ? r.iv / medianIv : 1);
    const beta = this.olsBeta(rangeRatios, ivRatioSeries);
    const ivAdjustment = this.clamp(1 + beta * (ivRatio - 1), 0.80, 1.25);
    let forecast = blendedBase * ivAdjustment * volatilityMultiplier * trendMultiplier;

    const sortedRanges = [...last20.map(r => r.range)].sort((a, b) => a - b);
    const p90 = this.percentile(sortedRanges, 0.90);
    if (forecast > p90) forecast = p90 * 0.70 + forecast * 0.30;

    return { forecastRange: forecast };
  }

  private async loadEconMap(): Promise<Map<string, EconRow[]>> {
    const q = query(collection(this.db, 'economicCalendar'), orderBy('date', 'asc'));
    const snap = await getDocs(q);
    const map = new Map<string, EconRow[]>();
    for (const d of snap.docs) {
      const row = d.data() as EconRow;
      const existing = map.get(row.date) ?? [];
      existing.push(row);
      map.set(row.date, existing);
    }
    return map;
  }

  private computeEcon(date: string, econByDate: Map<string, EconRow[]>): { multiplier: number } {
    const events = econByDate.get(this.normalizeToIso(date)) ?? [];
    const highCount = events.filter(e => this.isHighImpactEvent(e.economicEvent)).length;
    let multiplier = 1.0;
    if (highCount >= 2) multiplier = 1.18;
    else if (highCount === 1) multiplier = 1.10;
    else if (events.length > 0) multiplier = 1.05;
    return { multiplier };
  }

  private isHighImpactEvent(name: string): boolean {
    const n = name.toLowerCase();
    return ['fomc', 'federal funds', 'rate decision', 'non-farm', 'nonfarm', 'payroll', 'cpi', 'consumer price', 'ppi', 'producer price', 'gdp', 'pce'].some(k => n.includes(k));
  }

  private normalizeToIso(date: string): string {
    const trimmed = date.trim();
    const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
    if (slashMatch) {
      return `${slashMatch[3]}-${slashMatch[1].padStart(2, '0')}-${slashMatch[2].padStart(2, '0')}`;
    }
    return trimmed;
  }

  private computeEwma(rangesOldestFirst: number[], lambda: number): number {
    if (rangesOldestFirst.length === 0) return 0;
    let ewma = rangesOldestFirst[0];
    for (let i = 1; i < rangesOldestFirst.length; i++) {
      ewma = lambda * ewma + (1 - lambda) * rangesOldestFirst[i];
    }
    return ewma;
  }

  private median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  }

  private stdDev(values: number[]): number {
    if (values.length < 2) return 0;
    const mean = this.avg(values);
    return Math.sqrt(this.avg(values.map(v => (v - mean) ** 2)));
  }

  private olsBeta(y: number[], x: number[]): number {
    const n = Math.min(y.length, x.length);
    if (n < 5) return 1;
    const xSlice = x.slice(0, n);
    const ySlice = y.slice(0, n);
    const xMean = this.avg(xSlice);
    const yMean = this.avg(ySlice);
    let cov = 0, varX = 0;
    for (let i = 0; i < n; i++) {
      cov  += (xSlice[i] - xMean) * (ySlice[i] - yMean);
      varX += (xSlice[i] - xMean) ** 2;
    }
    return varX > 0 ? this.clamp(cov / varX, 0.3, 2.5) : 1;
  }

  private percentile(sortedValues: number[], p: number): number {
    if (sortedValues.length === 0) return 0;
    const index = p * (sortedValues.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    if (lower === upper) return sortedValues[lower];
    return sortedValues[lower] * (upper - index) + sortedValues[upper] * (index - lower);
  }

  private trendSetup(
    rows: RangeEstimationRow[],
    latest: RangeEstimationRow,
    avgRangePercent10: number,
    volatility: 'Expanding' | 'Contracting' | 'Normal',
  ): 'Bull Trend' | 'Bear Trend' | 'Range Expansion' | 'Compression' | 'Balanced' {
    const upDays = rows.filter(r => r.closeToCloseMove > 0).length;
    const downDays = rows.filter(r => r.closeToCloseMove < 0).length;
    const latestMovePercent = latest.previousClose > 0 ? (latest.closeToCloseMove / latest.previousClose) * 100 : 0;
    if (upDays >= 4 && latestMovePercent > 0) return 'Bull Trend';
    if (downDays >= 4 && latestMovePercent < 0) return 'Bear Trend';
    if (volatility === 'Expanding') return 'Range Expansion';
    if (avgRangePercent10 > 0 && this.avg(rows.map(r => this.rowRangePercent(r))) < avgRangePercent10 * 0.88) return 'Compression';
    return 'Balanced';
  }

  private volatilityState(
    latest: RangeEstimationRow,
    avgRangePercent5: number,
    avgRangePercent10: number,
  ): 'Expanding' | 'Contracting' | 'Normal' {
    const latestRangePercent = this.rowRangePercent(latest);
    if (avgRangePercent10 > 0 && (avgRangePercent5 > avgRangePercent10 * 1.12 || latestRangePercent > avgRangePercent10 * 1.18)) return 'Expanding';
    if (avgRangePercent10 > 0 && (avgRangePercent5 < avgRangePercent10 * 0.88 || latestRangePercent < avgRangePercent10 * 0.82)) return 'Contracting';
    return 'Normal';
  }

  private rowRangePercent(row: RangeEstimationRow): number {
    return row.previousClose > 0 ? (row.range / row.previousClose) * 100 : 0;
  }

  private async loadForecastDailyIv(ticker: string, forecastDate: string): Promise<number | null> {
    if (!forecastDate) return null;
    const normalized = ticker.trim().toUpperCase();
    try {
      const rows = await this.marketLevels.loadTickerHistory('daily', normalized, [forecastDate]);
      const match = rows.find(r => r.ticker === normalized && r.date === forecastDate);
      if (typeof match?.iv === 'number' && Number.isFinite(match.iv) && match.iv > 0) return match.iv;
    } catch { /* fall through */ }
    try {
      const rows = await firstValueFrom(this.csv.load('assets/data/archive/daily.csv'));
      const match = rows.find(r => r.ticker === normalized && r.date === forecastDate);
      return typeof match?.iv === 'number' && Number.isFinite(match.iv) && match.iv > 0 ? match.iv : null;
    } catch {
      return null;
    }
  }

  nextTradingDate(dateValue: string): string {
    const date = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(date.getTime())) return '';
    const holidays = this.holidayCache?.dates ?? new Set<string>();
    while (true) {
      date.setDate(date.getDate() + 1);
      const iso = this.toIso(date);
      if (date.getDay() !== 0 && date.getDay() !== 6 && !holidays.has(iso)) return iso;
    }
  }

  previousTradingDate(from: Date): string {
    const date = new Date(from);
    const holidays = this.holidayCache?.dates ?? new Set<string>();
    while (true) {
      date.setDate(date.getDate() - 1);
      const iso = this.toIso(date);
      if (date.getDay() !== 0 && date.getDay() !== 6 && !holidays.has(iso)) return iso;
    }
  }

  private toIso(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private async loadHolidayDates(): Promise<void> {
    if (this.holidayCache && Date.now() - this.holidayCache.ts < ECON_TTL_MS) return;
    try {
      const snap = await getDocs(collection(this.db, 'holidayTradingHours'));
      const list = snap.docs
        .map(d => { const r = d.data() as { referenceDate: string; name?: string }; return { date: r.referenceDate, name: r.name ?? r.referenceDate }; })
        .sort((a, b) => b.date.localeCompare(a.date));
      const dates = new Set(list.map(h => h.date));
      this.holidayCache = { dates, list, ts: Date.now() };
    } catch { /* non-critical — fall back to weekend-only */ }
  }

  async getHolidays(): Promise<{ date: string; name: string }[]> {
    await this.loadHolidayDates();
    return this.holidayCache?.list ?? [];
  }

  weekBeforeDates(holidayIso: string): string[] {
    const holidays = this.holidayCache?.dates ?? new Set<string>();
    const holiday = new Date(`${holidayIso}T00:00:00`);
    const dow = holiday.getDay(); // 0=Sun,1=Mon,...,6=Sat
    // Find Monday of the holiday week; if holiday is Monday, use previous Monday
    const daysBack = dow === 1 ? 7 : (dow === 0 ? 6 : dow - 1);
    const monday = new Date(holiday);
    monday.setDate(monday.getDate() - daysBack);
    const result: string[] = [];
    const cursor = new Date(monday);
    while (cursor < holiday) {
      const iso = this.toIso(cursor);
      const d = cursor.getDay();
      if (d !== 0 && d !== 6 && !holidays.has(iso)) result.push(iso);
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }

  dateValue(date: string): number {
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(date.trim());
    if (!match) return Date.parse(date) || 0;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private avg(values: number[]): number {
    const clean = values.filter(v => Number.isFinite(v));
    return clean.length ? clean.reduce((s, v) => s + v, 0) / clean.length : 0;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  formatNumber(value: number | null, digits = 2): string {
    return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '-';
  }
}
